import React, { FC, useEffect } from 'react';
import { View } from 'react-native';
import { OutPortal } from 'react-reverse-portal';
import { RadialGradientBackground } from '@components';
import { useRecoilState } from 'recoil';
import { PortalDestination } from 'rn-native-portals';

import { Theme } from '@constants';
import { helpers } from '@helpers';
import { useContextReference, useIsVideoFocused, useVideoNodePortal } from '@hooks';
import { liveVideoPlayerState } from '@store';

import { getUnitsForPosition } from './helpers';
import { styles } from './styles';
import { PIPWidgetProps } from './types';

export const PIPWidgetTestIds = {
  gradient: 'PIPWidgetTestIdsGradient',
};

export const PIPWidget: FC<PIPWidgetProps> = ({ position, testID, height, width, videoContext }) => {
  const isVideoFocused = useIsVideoFocused(videoContext);
  const [{ data, isLoading, error, adTrackingData }, setLiveVideoState] = useRecoilState(liveVideoPlayerState);
  const liveVideoPlayerNode = useVideoNodePortal();
  const positionUnits = getUnitsForPosition(position);

  const { isAndroid, isWeb } = helpers;

  useEffect(() => {
    if (isVideoFocused) {
      setLiveVideoState(currentState => ({
        ...currentState,
        width,
        height,
        stopped: false,
        ...(isAndroid && {
          isFullScreen: false,
          isScreenFocused: true,
          nextFocusUp: undefined,
        }),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoFocused]);

  return (
    <View
      collapsable={false}
      importantForAccessibility="no-hide-descendants"
      ref={useContextReference(testID)}
      style={[
        // TODO: figure out how to remove inline styles and accept props in stylesheet w/o errors
        {
          /**
           * 12px is added to dimensions of the container to account for the focus border style
           * Per comps, borders appear outside of the element's actual dimensions
           */
          height: Theme.px(height + 12),
          width: Theme.px(width + 12),
        },
        styles.pipWrapper,
        positionUnits,
      ]}
      testID={testID}
    >
      {isVideoFocused ? (
        <RadialGradientBackground>
          <View testID={PIPWidgetTestIds.gradient} collapsable={false} style={styles.radialGradientBackgroundContainer}>
            {isWeb ? (
              <OutPortal
                adData={adTrackingData}
                data={data}
                error={error}
                height={Theme.px(height)}
                isFullScreen={false}
                isLoading={isLoading}
                isScreenFocused
                node={liveVideoPlayerNode}
                translation={{
                  x: positionUnits?.left
                    ? positionUnits.left
                    : positionUnits?.right
                    ? Theme.appWidth - positionUnits.right - Theme.px(width)
                    : 0,
                  y: positionUnits?.top,
                }}
                width={Theme.px(width)}
              />
            ) : (
              <PortalDestination
                collapsable={false}
                height={height}
                name={'liveVideoPlayer'}
                style={{ width: Theme.px(width), height: Theme.px(height) }}
                width={width}
              />
            )}
          </View>
        </RadialGradientBackground>
      ) : null}
    </View>
  );
};
